import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress,
  Snackbar, Tooltip, Divider, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add, Edit, Delete, DragIndicator, CampaignOutlined,
  ToggleOn, ToggleOff,
} from '@mui/icons-material';
import {
  fetchPromoMessages, createPromoMessage, updatePromoMessage,
  deletePromoMessage, reorderPromoMessages,
} from '../../api/api';
import api from '../../api/api';
import { io } from 'socket.io-client';

const ICON_OPTIONS = [
  { value: 'car-outline',              label: 'Delivery / Car' },
  { value: 'cash-outline',             label: 'Cash / Payment' },
  { value: 'flash-outline',            label: 'Flash / Bolt' },
  { value: 'sparkles-outline',         label: 'Sparkles / New' },
  { value: 'shield-checkmark-outline', label: 'Shield / Guarantee' },
  { value: 'gift-outline',             label: 'Gift' },
  { value: 'ribbon-outline',           label: 'Ribbon / Award' },
  { value: 'megaphone-outline',        label: 'Megaphone' },
  { value: 'star-outline',             label: 'Star' },
  { value: 'timer-outline',            label: 'Timer / Limited' },
  { value: 'pricetag-outline',         label: 'Price Tag' },
  { value: 'bag-handle-outline',       label: 'Shopping Bag' },
];

const EMPTY_FORM = { text: '', icon: 'megaphone-outline', active: true };

export default function PromoManager() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMsg, setEditMsg] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  // DnD
  const [dragId, setDragId] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [hoverSide, setHoverSide] = useState(null);

  const suppressSocket = useRef(0);
  const skipNext = () => { suppressSocket.current += 1; };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetchPromoMessages();
      setMessages(res.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const socket = io(api.defaults.baseURL, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });
    socket.on('promo:updated', () => {
      if (suppressSocket.current > 0) { suppressSocket.current -= 1; return; }
      load();
    });
    return () => { socket.off('promo:updated'); socket.disconnect(); };
  }, [load]);

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const openAdd = () => {
    setEditMsg(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (msg) => {
    setEditMsg(msg);
    setForm({ text: msg.text, icon: msg.icon || 'megaphone-outline', active: msg.active !== false });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.text.trim()) return showSnack('Message text is required', 'error');
    setSaving(true);
    skipNext();
    try {
      if (editMsg) {
        await updatePromoMessage(editMsg._id, form);
        showSnack('Message updated');
      } else {
        await createPromoMessage(form);
        showSnack('Message created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      suppressSocket.current = Math.max(0, suppressSocket.current - 1);
      showSnack(e.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (msg) => {
    skipNext();
    try {
      await updatePromoMessage(msg._id, { active: !msg.active });
      showSnack(msg.active ? 'Message deactivated' : 'Message activated');
      load();
    } catch {
      suppressSocket.current = Math.max(0, suppressSocket.current - 1);
      showSnack('Toggle failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    skipNext();
    try {
      await deletePromoMessage(id);
      setDeleteConfirm(null);
      showSnack('Message deleted');
      load();
    } catch {
      suppressSocket.current = Math.max(0, suppressSocket.current - 1);
      showSnack('Delete failed', 'error');
    }
  };

  // ── Drag and Drop ────────────────────────────────────────────
  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id === dragId) { setHoverId(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const side = e.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom';
    setHoverId(id);
    setHoverSide(side);
  };

  const onDragLeave = (e, id) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (hoverId === id) { setHoverId(null); setHoverSide(null); }
  };

  const onDrop = async (e, targetId) => {
    e.preventDefault();
    setHoverId(null);
    setHoverSide(null);
    if (!dragId || dragId === targetId) return;

    const arr = [...messages];
    const fromIdx = arr.findIndex(m => m._id === dragId);
    const toIdx   = arr.findIndex(m => m._id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;

    const [moved] = arr.splice(fromIdx, 1);
    const insertAt = hoverSide === 'bottom' ? toIdx + (fromIdx < toIdx ? 0 : 1) : toIdx - (fromIdx > toIdx ? 0 : 1);
    arr.splice(Math.max(0, insertAt), 0, moved);

    setMessages(arr);
    skipNext();
    try {
      await reorderPromoMessages(arr.map(m => m._id));
    } catch {
      showSnack('Reorder failed', 'error');
      suppressSocket.current = Math.max(0, suppressSocket.current - 1);
      load();
    }
  };

  const onDragEnd = () => { setDragId(null); setHoverId(null); setHoverSide(null); };

  const dropShadow = (id) => {
    if (hoverId !== id) return 'none';
    return hoverSide === 'top'
      ? 'inset 0 3px 0 0 #667eea'
      : 'inset 0 -3px 0 0 #667eea';
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">Promo Strip</Typography>
          <Typography variant="body2" color="text.secondary">
            Rotating messages shown at the top of the home screen — drag to reorder
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}
          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700 }}>
          Add Message
        </Button>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && messages.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'background.paper' }}>
          <CampaignOutlined sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No promo messages yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add messages to display in the rotating promo strip
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            Add Message
          </Button>
        </Paper>
      )}

      {!loading && messages.length > 0 && (
        <Stack spacing={1.5}>
          {messages.map((msg, index) => (
            <Paper
              key={msg._id}
              draggable
              onDragStart={e => onDragStart(e, msg._id)}
              onDragOver={e => onDragOver(e, msg._id)}
              onDragLeave={e => onDragLeave(e, msg._id)}
              onDrop={e => onDrop(e, msg._id)}
              onDragEnd={onDragEnd}
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                cursor: 'grab',
                opacity: dragId === msg._id ? 0.4 : 1,
                boxShadow: dropShadow(msg._id),
                transition: 'opacity 0.15s, box-shadow 0.12s',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicator sx={{ color: 'text.disabled', flexShrink: 0 }} />

              {/* Position badge */}
              <Chip
                label={`#${index + 1}`}
                size="small"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              />

              {/* Icon label */}
              <Typography variant="caption" sx={{
                px: 1, py: 0.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontFamily: 'monospace',
                flexShrink: 0,
                fontSize: 11,
              }}>
                {msg.icon}
              </Typography>

              {/* Text */}
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }} noWrap>
                {msg.text}
              </Typography>

              {/* Status */}
              <Chip
                label={msg.active !== false ? 'Active' : 'Off'}
                size="small"
                color={msg.active !== false ? 'success' : 'default'}
                sx={{ flexShrink: 0 }}
              />

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <Tooltip title={msg.active !== false ? 'Deactivate' : 'Activate'}>
                  <IconButton size="small" onClick={() => handleToggle(msg)} color={msg.active !== false ? 'success' : 'default'}>
                    {msg.active !== false ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEdit(msg)} color="primary">
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => setDeleteConfirm(msg)} color="error">
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{editMsg ? 'Edit Message' : 'Add Message'}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Message Text *"
              value={form.text}
              onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              placeholder="e.g. Free delivery on orders over KSh 3,000"
            />
            <FormControl fullWidth>
              <InputLabel>Icon</InputLabel>
              <Select value={form.icon} label="Icon" onChange={e => setForm(f => ({ ...f, icon: e.target.value }))}>
                {ICON_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />}
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} /> : (editMsg ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={800}>Delete Message</DialogTitle>
        <DialogContent>
          <Typography>Delete this message? This cannot be undone.</Typography>
          {deleteConfirm && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              "{deleteConfirm.text}"
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm?._id)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
